<script setup>
import { computed } from "vue";
import ProfileViewTabs from "@/widgets/user-profile-drawer/ui/ProfileViewTabs.vue";

const props = defineProps({
  data: { type: Object, required: true },
  tab: { type: String, required: true },
  editMode: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
  isOwn: { type: Boolean, default: false },
  canEdit: { type: Boolean, default: false },
  showViewSwitcher: { type: Boolean, default: false },
  showActions: { type: Boolean, default: true },
});

defineEmits(["set-tab", "start-edit", "cancel", "switch-view"]);

const TABS = [
  { id: "personal", label: "Основные", svg: "huge-user" },
  { id: "documents", label: "Документы", svg: "sli-identification" },
  { id: "education", label: "Образование", svg: "sli-education" },
  { id: "address", label: "Адреса", svg: "sli-main" },
  { id: "activity", label: "Деятельность", svg: "sli-case" },
  { id: "license", label: "Лицензия", svg: "sli-medal" },
  { id: "history", label: "История", svg: "huge-journal" },
  { id: "services", label: "Услуги", svg: "sli-services" },
  { id: "system", label: "Система", svg: "sli-system" },
];

const fullName = computed(
  () =>
    [props.data.last_name, props.data.first_name].filter(Boolean).join(" ") ||
    "—"
);

const initials = computed(() => {
  const a = props.data.first_name?.[0] ?? "";
  const b = props.data.last_name?.[0] ?? "";
  return (a + b).toUpperCase() || "—";
});
</script>

<template>
  <aside
    class="w-[290px] shrink-0 bg-card border-r border-border pb-5 flex flex-col gap-2 overflow-y-auto"
  >
    <!-- side-head card -->
    <div class="relative overflow-hidden bg-card">
      <!-- edit icon top-right -->
      <template v-if="showActions">
        <el-tooltip
          v-if="!editMode"
          :content="$t('Редактировать')"
          :show-after="300"
          placement="left"
        >
          <button
            type="button"
            class="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-card border border-border text-foreground flex items-center justify-center hover:bg-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="!canEdit"
            @click="$emit('start-edit')"
          >
            <SvgIcon name="huge-edit" class="w-3.5 h-3.5" style="fill: none" />
          </button>
        </el-tooltip>
        <el-tooltip
          v-else
          :content="$t('Отменить редактирование')"
          :show-after="300"
          placement="left"
        >
          <button
            type="button"
            class="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-card border border-border text-foreground flex items-center justify-center hover:bg-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="saving"
            @click="$emit('cancel')"
          >
            <SvgIcon name="huge-close" class="w-3.5 h-3.5" style="fill: none" />
          </button>
        </el-tooltip>
      </template>

      <!-- top banner -->
      <div class="h-24 bg-muted border-0 border-b border-border" />

      <!-- bottom content -->
      <div class="px-4 pt-0 pb-2 -mt-10">
        <div
          class="relative w-16 h-16 rounded-full flex items-center justify-center text-white text-[18px] font-semibold overflow-visible mb-3 ring-4 ring-card"
          :style="{
            background: 'linear-gradient(135deg, #d4c45d 0%, #a08a3a 100%)',
          }"
        >
          <div
            class="w-full h-full rounded-full overflow-hidden flex items-center justify-center"
          >
            <img
              v-if="data.photo"
              :src="data.photo"
              alt=""
              class="w-full h-full object-cover"
            />
            <span v-else>{{ initials }}</span>
          </div>
          <span
            class="absolute right-0.5 bottom-0.5 w-3 h-3 rounded-full bg-green-500 border-2 border-card"
          />
        </div>

        <div class="text-foreground break-words">
          {{ fullName }}
        </div>
        <div class="text-sm text-muted-foreground mt-0.5">
          {{ $t("Нотариус") }}
        </div>
      </div>
    </div>

    <!--    <Separator />-->

    <!-- nav -->
    <nav class="flex flex-col py-2">
      <button
        v-for="t in TABS"
        :key="t.id"
        type="button"
        class="nav-link"
        :class="{ 'is-active': tab === t.id }"
        @click="$emit('set-tab', t.id)"
      >
        <div
          v-if="tab === t.id"
          class="absolute left-0 top-0 bottom-0 w-[3px] bg-foreground"
        />
        <SvgIcon :name="t.svg" style="width: 16px; height: 16px; fill: none" />
        <div>{{ $t(t.label) }}</div>
      </button>
    </nav>

    <div v-if="showActions && showViewSwitcher" class="mt-auto px-4 pt-2">
      <ProfileViewTabs
        current="notary"
        @switch="$emit('switch-view', $event)"
      />
    </div>
  </aside>
</template>

<style lang="scss" scoped>
.nav-link {
  @apply relative flex items-center gap-3.5 px-5 py-2 text-left text-sm transition-colors;
  color: hsl(var(--sidebar-foreground));

  &:hover {
    @apply bg-muted;
    color: hsl(var(--sidebar-accent-foreground));

    svg {
      color: hsl(var(--sidebar-accent-foreground));
    }
  }

  &.is-active {
    @apply bg-muted font-medium;
  }
}
</style>
